/**
 * The app's number formatters. They format, they never convert: energy conversion is the
 * engine's (`formatEnergy`, `kcalToKj` via app/src/engine.ts), per rule 1.
 *
 * Locale is the browser's own (`undefined`), so "1,840" or "1 840" or "1.840" as the person
 * expects. Missing or non-finite values render as an em dash.
 */
import {
  formatEnergy,
  formatMass,
  kcalToKj,
  perGToPerOz,
  type EnergyUnit,
  type MassUnit,
} from '../engine'

export const EM_DASH = '—'

/**
 * The mass unit figures are SHOWN in (storage is always grams). Set by `UnitsProvider` from
 * the person's setting as the tree renders; 'g' until then, so tests and stories need nothing.
 * Every gram figure goes through `formatGrams` / `formatDensity`, so one setting reaches the
 * food table, the plan, the list and the shop footer alike.
 */
let displayMass: MassUnit = 'g'

export function setDisplayMass(unit: MassUnit): void {
  displayMass = unit
}

export function getDisplayMass(): MassUnit {
  return displayMass
}

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n)

export function formatNumber(n: number | null | undefined, dp = 0, locale?: string): string {
  if (!isNum(n)) return EM_DASH
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(n)
}

/**
 * "1,255 g"; with `kg` ≥ 1000 g → "1.26 kg". In ounces (the setting, or `unit`): "8.8 oz",
 * and with `kg` ≥ 1 lb → "2 lb 12 oz".
 */
export function formatGrams(
  g: number | null | undefined,
  opts: { kg?: boolean; dp?: number; locale?: string; unit?: MassUnit } = {},
): string {
  if (!isNum(g)) return EM_DASH
  if ((opts.unit ?? displayMass) === 'oz') return formatMass(g, 'oz', { big: opts.kg === true })
  if (opts.kg && Math.abs(g) >= 1000)
    return `${formatNumber(g / 1000, opts.dp ?? 2, opts.locale)} kg`
  return `${formatNumber(g, opts.dp ?? 0, opts.locale)} g`
}

/** Energy in the person's unit, via the engine. */
export function formatKcalAs(kcal: number | null | undefined, unit: EnergyUnit): string {
  if (!isNum(kcal)) return EM_DASH
  return formatEnergy(kcal, unit)
}

/** Energy density: "4.57 kcal/g" or "19.1 kJ/g"; in ounces "130 kcal/oz" or "542 kJ/oz". */
export function formatDensity(
  kcalPerG: number | null | undefined,
  unit: EnergyUnit,
  mass: MassUnit = displayMass,
): string {
  if (!isNum(kcalPerG)) return EM_DASH
  if (mass === 'oz') {
    const perOz = perGToPerOz(kcalPerG)
    return unit === 'kJ'
      ? `${formatNumber(kcalToKj(perOz), 0)} kJ/oz`
      : `${formatNumber(perOz, 0)} kcal/oz`
  }
  return unit === 'kJ'
    ? `${formatNumber(kcalToKj(kcalPerG), 1)} kJ/g`
    : `${formatNumber(kcalPerG, 2)} kcal/g`
}

export type CurrencyDisplay = 'symbol' | 'code'

/** "$12.40", "€3.10", "¥480"; with `code`, "AUD 12.40". */
export function formatMoney(
  amount: number | null | undefined,
  currency: string,
  display: CurrencyDisplay = 'symbol',
  locale?: string,
): string {
  if (!isNum(amount)) return EM_DASH
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: display === 'code' ? 'code' : 'narrowSymbol',
    }).format(amount)
  } catch {
    // An unknown currency code: plain number and the code.
    return `${currency} ${formatNumber(amount, 2, locale)}`
  }
}

/** 0.234 → "23%". */
export function formatPercent(share: number | null | undefined, dp = 0): string {
  if (!isNum(share)) return EM_DASH
  return `${formatNumber(share * 100, dp)}%`
}

/** The per-mass suffix for column headings: "g" or "oz". */
export function massSuffix(mass: MassUnit = displayMass): string {
  return mass === 'oz' ? 'oz' : 'g'
}
